// src/features/explore/ExploreChips.tsx — single-row chip selector (content-type + topic).
import styled from 'styled-components';
import { quietScrollbar } from '@/theme/scrollbar';
import { CONTENT_CHIPS } from './meta';
import type { ExploreTopic } from '@/core/models/explore';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 0;
  overflow-x: auto;
  ${quietScrollbar}
  padding: 3px 2px 8px;
`;

const Chip = styled.button<{ $active: boolean }>`
  cursor: pointer;
  padding: 6px 14px;
  flex-shrink: 0;
  border-radius: ${({ theme }) => theme.radius.md};
  font-family: ${({ theme }) => theme.font.body};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ $active }) => ($active ? 700 : 500)};
  border: 1px solid ${({ theme, $active }) => ($active ? 'transparent' : theme.color.glassBorder)};
  color: ${({ theme, $active }) => ($active ? theme.color.onAccent : theme.color.textPrimary)};
  background: ${({ theme, $active }) => ($active ? theme.color.accentCyan : theme.color.glassBg)};
  transition:
    border-color 120ms ease,
    background 120ms ease;
  &:hover {
    filter: brightness(1.06);
  }
  &:focus-visible {
    outline: 2px solid ${({ theme }) => theme.color.accentCyan};
    outline-offset: 2px;
  }
`;

export interface ExploreChipsProps {
  topics: ExploreTopic[];
  /** active chip id: a content-type ('all'|'posts'|'people'|'communities') or a topic id. */
  active: string;
  onChange: (id: string) => void;
}

export function ExploreChips({ topics, active, onChange }: ExploreChipsProps) {
  // The seed "all" topic is represented by the content-type "All" chip — drop it.
  const topicChips = topics.filter((t) => t.id !== 'all');

  return (
    <Row role="group" aria-label="Explore filters">
      {CONTENT_CHIPS.map((c) => (
        <Chip
          key={c.id}
          type="button"
          $active={active === c.id}
          aria-pressed={active === c.id}
          onClick={() => onChange(c.id)}
        >
          {c.label}
        </Chip>
      ))}
      {topicChips.map((t) => (
        <Chip
          key={t.id}
          type="button"
          $active={active === t.id}
          aria-pressed={active === t.id}
          onClick={() => onChange(t.id)}
        >
          {t.label}
        </Chip>
      ))}
    </Row>
  );
}
